import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Fab, IconButton, Tooltip } from '@material-ui/core';
import { Add, Delete, Refresh, SyncAlt } from '@material-ui/icons';
import { BackButton, hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import MaterialTable from 'material-table';
import * as actions from './store/colliActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';
import ChooseColliType from './ChooseColliType';
import { snackActions } from '../../sc-utilities/SnackBar';

const styles = theme => ({
    "@global": {
        "[class*='buttonsCustomer']": {
            margin: theme.spacing(),
            fontSize: '13px',
            width: '100%',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.buttonText.default
            },
        },
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.buttonText.default,
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
let countColli = 0

const ColliList = props => {
    const { classes, translations, goToDeleteColli, goToCreateColli, permissions, onColliCount, warehouseActive, onFetchColliList, setWarehouseActive, activeWarehouseID, fetching, saveColumnsFilter, activeCustomer, tableColumns, listColliTypes, tableRef } = props;

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [chooseColliTypeModalState, setChooseColliTypeModalState] = useState({ open: false, data: [] })
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ['colli_delete']),
        selectionProps: rowData => {
            return { id: `checkbox_${rowData.ColliID}` }
        },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = { WarehouseID: warehouseActive.name[0] ? warehouseActive.name[0].value : warehouseActive.data[0] ? warehouseActive.data[0].value : activeWarehouseID };
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };
    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        query['q']['LockedType'] = query.q.LockedType instanceof Array && query.q.LockedType.length > 0 ? query.q.LockedType : null;
        query['q']['Grade'] = query.q.Grade instanceof Array && query.q.Grade.length > 0 ? query.q.Grade : null;
        query['q']['ProductTypeKeys'] = query.q.ProductTypeKeys instanceof Array && query.q.ProductTypeKeys.length > 0 ? query.q.ProductTypeKeys : null;

        let count = await onColliCount(query);
        countColli = count
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = await onFetchColliList(query);
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const openModal = async () => {
        let data = await listColliTypes({ ActiveCustomerID: activeCustomer, q: { WarehouseID: activeWarehouseID } })
        if (data instanceof Array && data.length < 1) {
            snackActions.snackBarComponent(`${translations['colli_types_found'] || "No colli types found, please create at least one colli type first"}`, "info");
            return
        }
        if (data.length === 1) {
            goToCreateColli(data[0].value, warehouseActive);
            return

        }
        else {
            setChooseColliTypeModalState({ open: true, data: data })
        }
    };

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography >{translations['stock'] || 'Stock'}-{translations['warehouse'] || 'Warehouse'} ({warehouseActive.name.length > 0 ? warehouseActive.name[0].displayValue : ''})</Typography>
                <Tooltip title={translations["change_warehouse_id"] || "Change active warehouse"}>
                    <IconButton aria-label={translations["change_warehouse_id"] || "Change active warehouse"} component="div" disabled={fetching} onClick={() => setWarehouseActive({ ...warehouseActive, open: true })}>
                        <SyncAlt id={'refresh_button'} />
                    </IconButton>
                </Tooltip>
                {
                    hasPermission(permissions, 'colli_create') ?
                        <Fab
                            variant="extended"
                            size="small"
                            id="create_colli"
                            color="primary"
                            style={{ borderRadius: '1px' }}
                            aria-label="Add"
                            className={classes.fab}
                            onClick={() => openModal(`/colli/create`)}
                        >
                            <Add size="small" />
                            {translations["create_new"] || "Create new"}
                        </Fab> : null
                }
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh id={'refresh_button'} />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    const handleBulkDelete = colliIDS => {
        if (colliIDS.length === countColli) {
            snackActions.snackBarComponent(translations['cannot_delete_all'] || 'Cannot delete all collies', 'warning');
            return;
        }
        goToDeleteColli(colliIDS);
    };

    // Table Settings
    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["collies"] || "Collies"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(colli => colli.ColliID))
        }
    ]

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`Colli`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {chooseColliTypeModalState.open ? <ChooseColliType handleClose={() => setChooseColliTypeModalState({ open: false, data: [] })} warehouses={warehouseActive} colliType={chooseColliTypeModalState} /> : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        activeWarehouseID: state.core.login.user.ActiveWarehouseID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        colli: state.logistic.colli,
        fetching: state.logistic.colli.fetching,
        deleting: state.logistic.colli.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {

        onColliCount: filter => dispatch(actions.countColli(filter)),
        onFetchColliList: filter => dispatch(actions.fetchColli(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        listColliTypes: data => dispatch(actions.listColliTypes(data))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ColliList));

